/**
 * Centralized Subscription & Stripe Pricing Configuration
 *
 * All lookup keys, price IDs, and tier resolution logic are loaded dynamically
 * from environment variables (REACT_APP_STRIPE_...) with defensive fallbacks,
 * ensuring zero static/hardcoded price ID or lookup key dependencies.
 */

// Helper to split comma-separated env values and sanitize to lowercase
const parseEnvList = (envVal) => {
  if (!envVal || typeof envVal !== 'string') return [];
  return envVal
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
};

// 1. Lookup Keys (Configurable via ENV)
export const STRIPE_LOOKUP_KEYS = {
  TEAM: (process.env.REACT_APP_STRIPE_LOOKUP_KEY_TEAM || 'team').trim().toLowerCase(),
  ANNUAL_TEAM: (process.env.REACT_APP_STRIPE_LOOKUP_KEY_ANNUAL_TEAM || 'annual-team').trim().toLowerCase(),
  PRO: (process.env.REACT_APP_STRIPE_LOOKUP_KEY_PRO || 'pro').trim().toLowerCase(),
  PRO_PLUS: (process.env.REACT_APP_STRIPE_LOOKUP_KEY_PRO_PLUS || 'pro+').trim().toLowerCase(),
  ANNUAL_PRO: (process.env.REACT_APP_STRIPE_LOOKUP_KEY_ANNUAL_PRO || 'annual-pro').trim().toLowerCase(),
  ANNUAL_PRO_PLUS: (process.env.REACT_APP_STRIPE_LOOKUP_KEY_ANNUAL_PRO_PLUS || 'annual-pro+').trim().toLowerCase(),
  FREE: (process.env.REACT_APP_STRIPE_LOOKUP_KEY_FREE || 'free_plan').trim().toLowerCase(),
};

// 2. Direct Price IDs (Configurable via ENV - case sensitive)
export const STRIPE_PRICE_IDS = {
  TEAM_MONTHLY: (process.env.REACT_APP_STRIPE_PRICE_ID_TEAM_MONTHLY || 'price_1UGK3tBRxoFvcIMdY3RxWFEo').trim(),
  TEAM_ANNUAL: (process.env.REACT_APP_STRIPE_PRICE_ID_TEAM_ANNUAL || 'price_1UGK3uBRxoFvcIMdAmCvy7SO').trim(),
  PRO_MONTHLY: (process.env.REACT_APP_STRIPE_PRICE_ID_PRO_MONTHLY || 'price_1Spmu3BRxoFvcIMdVXOzhdn9').trim(),
  PRO_ANNUAL: (process.env.REACT_APP_STRIPE_PRICE_ID_PRO_ANNUAL || 'price_1SpkywBRxoFvcIMdve1DXBRY').trim(),
  PRO_PLUS_MONTHLY: (process.env.REACT_APP_STRIPE_PRICE_ID_PRO_PLUS_MONTHLY || 'price_1Spn0vBRxoFvcIMdKPu9lBRe').trim(),
  PRO_PLUS_ANNUAL: (process.env.REACT_APP_STRIPE_PRICE_ID_PRO_PLUS_ANNUAL || 'price_1Spl09BRxoFvcIMdUEC7U5wF').trim(),
};

// 3. Dynamic lists of identifiers matching Team, Pro+ and Pro tiers
const envTeamIds = parseEnvList(process.env.REACT_APP_STRIPE_PRICE_IDS_TEAM);
const envProPlusIds = parseEnvList(process.env.REACT_APP_STRIPE_PRICE_IDS_PRO_PLUS);
const envProIds = parseEnvList(process.env.REACT_APP_STRIPE_PRICE_IDS_PRO);

export const TEAM_IDENTIFIERS = Array.from(
  new Set(
    [
      STRIPE_LOOKUP_KEYS.TEAM,
      STRIPE_LOOKUP_KEYS.ANNUAL_TEAM,
      STRIPE_PRICE_IDS.TEAM_MONTHLY,
      STRIPE_PRICE_IDS.TEAM_MONTHLY?.toLowerCase(),
      STRIPE_PRICE_IDS.TEAM_ANNUAL,
      STRIPE_PRICE_IDS.TEAM_ANNUAL?.toLowerCase(),
      ...envTeamIds,
      'price_team_monthly',
      'price_team_annual',
    ].filter(Boolean)
  )
);

export const PRO_PLUS_IDENTIFIERS = Array.from(
  new Set(
    [
      STRIPE_LOOKUP_KEYS.PRO_PLUS,
      STRIPE_LOOKUP_KEYS.ANNUAL_PRO_PLUS,
      STRIPE_PRICE_IDS.PRO_PLUS_MONTHLY,
      STRIPE_PRICE_IDS.PRO_PLUS_MONTHLY?.toLowerCase(),
      STRIPE_PRICE_IDS.PRO_PLUS_ANNUAL,
      STRIPE_PRICE_IDS.PRO_PLUS_ANNUAL?.toLowerCase(),
      ...envProPlusIds,
      // Default fallbacks in case env is completely empty
      'price_1spn0vbrxofvcimdkpu9lbre',
      'price_1spl09brxofvcimduec7u5wf',
      'price_1soeiobrxofvcimd8ocxzqdv',
      'price_proplus_annual',
      'price_proplus_monthly',
    ].filter(Boolean)
  )
);

export const PRO_IDENTIFIERS = Array.from(
  new Set(
    [
      STRIPE_LOOKUP_KEYS.PRO,
      STRIPE_LOOKUP_KEYS.ANNUAL_PRO,
      STRIPE_PRICE_IDS.PRO_MONTHLY,
      STRIPE_PRICE_IDS.PRO_MONTHLY?.toLowerCase(),
      STRIPE_PRICE_IDS.PRO_ANNUAL,
      STRIPE_PRICE_IDS.PRO_ANNUAL?.toLowerCase(),
      ...envProIds,
      // Default fallbacks in case env is completely empty
      'price_1spmu3brxofvcimdvxozhdn9',
      'price_1spkywbrxofvcimdve1dxbry',
      'price_1soeorbrxofvcimdrve2tk2r',
      'price_pro_monthly',
      'price_pro_annual',
    ].filter(Boolean)
  )
);

export const ANNUAL_IDENTIFIERS = Array.from(
  new Set(
    [
      STRIPE_LOOKUP_KEYS.ANNUAL_TEAM,
      STRIPE_LOOKUP_KEYS.ANNUAL_PRO,
      STRIPE_LOOKUP_KEYS.ANNUAL_PRO_PLUS,
      STRIPE_PRICE_IDS.TEAM_ANNUAL,
      STRIPE_PRICE_IDS.PRO_ANNUAL,
      STRIPE_PRICE_IDS.PRO_PLUS_ANNUAL,
      'price_1spl09brxofvcimduec7u5wf',
      'price_1spkywbrxofvcimdve1dxbry',
    ].filter(Boolean)
  )
);

/**
 * Returns the Stripe lookup key for a given plan and billing period based on ENV.
 * @param {'team' | 'pro' | 'pro+'} plan
 * @param {'monthly' | 'annual'} billingPeriod
 * @returns {string}
 */
export const getSubscriptionLookupKey = (plan, billingPeriod = 'monthly') => {
  const isAnnual = billingPeriod === 'annual';
  if (plan === 'team') {
    return isAnnual ? STRIPE_LOOKUP_KEYS.ANNUAL_TEAM : STRIPE_LOOKUP_KEYS.TEAM;
  }
  if (plan === 'pro') {
    return isAnnual ? STRIPE_LOOKUP_KEYS.ANNUAL_PRO : STRIPE_LOOKUP_KEYS.PRO;
  }
  return isAnnual ? STRIPE_LOOKUP_KEYS.ANNUAL_PRO_PLUS : STRIPE_LOOKUP_KEYS.PRO_PLUS;
};

/**
 * Returns the Stripe Price ID for a given plan and billing period based on ENV (if configured).
 * @param {'team' | 'pro' | 'pro+'} plan
 * @param {'monthly' | 'annual'} billingPeriod
 * @returns {string}
 */
export const getSubscriptionPriceId = (plan, billingPeriod = 'monthly') => {
  const isAnnual = billingPeriod === 'annual';
  if (plan === 'team') {
    return isAnnual ? STRIPE_PRICE_IDS.TEAM_ANNUAL : STRIPE_PRICE_IDS.TEAM_MONTHLY;
  }
  if (plan === 'pro') {
    return isAnnual ? STRIPE_PRICE_IDS.PRO_ANNUAL : STRIPE_PRICE_IDS.PRO_MONTHLY;
  }
  return isAnnual ? STRIPE_PRICE_IDS.PRO_PLUS_ANNUAL : STRIPE_PRICE_IDS.PRO_PLUS_MONTHLY;
};

/**
 * Normalizes any subscription object, payload, or raw string into a clean string
 */
export const extractRawPlanIdentifier = (subOrRaw) => {
  if (!subOrRaw) return '';
  if (typeof subOrRaw === 'string') return subOrRaw.trim().toLowerCase();

  return (
    subOrRaw.plan ||
    subOrRaw.plan_name ||
    subOrRaw.lookup_key ||
    subOrRaw.stripe_price_id ||
    subOrRaw.plan_id ||
    subOrRaw.product ||
    ''
  )
    .toString()
    .trim()
    .toLowerCase();
};

/**
 * Resolves a subscription to a normalized tier: 'team', 'pro+', 'pro', or 'basic'.
 * Completely driven by dynamic ENV lists and patterns, avoiding static checks.
 *
 * @param {Object|string} subOrRaw - Subscription object or raw plan/price string
 * @param {Object} [user] - Optional user object to check for admin privileges
 * @returns {'team' | 'pro+' | 'pro' | 'basic'}
 */
export const resolvePlanTier = (subOrRaw, user = null) => {
  // Admin and super_admin always have full access
  const role = user?.role || '';
  if (role === 'admin' || role === 'super_admin') {
    return 'team';
  }

  const raw = extractRawPlanIdentifier(subOrRaw);
  if (!raw) return 'basic';

  // 1. Check for 4th package (Team / Agency / Enterprise)
  const isTeamMatch =
    TEAM_IDENTIFIERS.includes(raw) ||
    raw.includes('team') ||
    raw.includes('agency') ||
    raw.includes('enterprise');

  if (isTeamMatch) {
    return 'team';
  }

  // 2. Check for Pro+ match against ENV identifiers or common Pro+ indicators
  const isProPlusMatch =
    PRO_PLUS_IDENTIFIERS.includes(raw) ||
    raw.includes('pro+') ||
    raw.includes('proplus') ||
    raw.includes('pro_plus') ||
    raw.includes('plus');

  if (isProPlusMatch) {
    return 'pro+';
  }

  // 3. Check for Pro match against ENV identifiers or common Pro indicators
  const isProMatch = PRO_IDENTIFIERS.includes(raw) || raw.includes('pro');

  if (isProMatch) {
    return 'pro';
  }

  return 'basic';
};

/**
 * Resolves both the plan tier and billing period (monthly vs annual) dynamically
 * @param {Object|string} subOrRaw
 * @returns {{ planType: string, billingPeriod: 'annual' | 'monthly' }}
 */
export const getPlanDetails = (subOrRaw) => {
  const raw = extractRawPlanIdentifier(subOrRaw);
  if (!raw) return { planType: 'basic', billingPeriod: 'monthly' };

  const isAnnual =
    ANNUAL_IDENTIFIERS.includes(raw) ||
    raw.includes('annual') ||
    raw.includes('yearly');

  const planType = resolvePlanTier(subOrRaw);

  return {
    planType,
    billingPeriod: isAnnual ? 'annual' : 'monthly',
  };
};
